import { formatLongDate, formatTime } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, FormControlLabel, Radio, RadioGroup, Stack, TextField } from "@mui/material";
import { useSendWorkerCancelAlert } from "@src/appV2/api/slack/useSendWorkerCancelAlert";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { logEvent } from "@src/appV2/lib/analytics";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { showErrorToast, showInfoToast } from "@src/appV2/lib/Notifications";
import { Avatar } from "@src/appV2/redesign/components/Avatar";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { Divider } from "@src/appV2/redesign/components/Divider";
import { ResponsiveDialog } from "@src/appV2/redesign/components/ResponsiveDialog";
import { Text } from "@src/appV2/redesign/components/Text";
import { TimeSlotAvatar } from "@src/appV2/redesign/Shift/TimeSlotAvatar";
import { type TimeSlot } from "@src/appV2/redesign/Shift/types";
import { type UseModalState } from "@src/lib/ui-react";
import { parseISO } from "date-fns";
import { useEffect, useState } from "react";

import { ReportAcknowledgmentSection } from "./ReportAcknowledgmentSection";

const MIN_REPORT_DESCRIPTION_LENGTH = 30;

export interface ShiftData {
  shiftId?: string;
  workerId?: string;
  workerName?: string;
  startTime: string;
  endTime: string;
  timeSlot?: TimeSlot;
  workerQualification?: string;
  timezone?: string;
}

export interface WorkerRequestedCancelDialogProps {
  modalState: UseModalState;
  shift?: ShiftData;
  workerId?: string;
  workerName?: string;
  workerAvatarUrl?: string;
  facilityId?: string;
  workerQualification?: string;
}

export function WorkerRequestedCancelDialog(props: WorkerRequestedCancelDialogProps) {
  const {
    modalState,
    shift,
    workerId: workerIdProperty,
    workerName: workerNameProperty,
    workerAvatarUrl,
    facilityId: facilityIdProperty,
    workerQualification: workerQualificationProperty,
  } = props;

  const { mutateAsync: sendWorkerCancelAlert, isLoading } = useSendWorkerCancelAlert();
  const { user, profile } = useSession();
  const [shouldReplaceWorker, setShouldReplaceWorker] = useState(true);
  const [reportDescription, setReportDescription] = useState("");
  const [reportAcknowledged, setReportAcknowledged] = useState(false);

  useEffect(() => {
    if (!modalState.modalIsOpen) {
      setReportDescription("");
      setReportAcknowledged(false);
      setShouldReplaceWorker(true);
    }
  }, [modalState.modalIsOpen]);

  const isDescriptionValid = reportDescription.length >= MIN_REPORT_DESCRIPTION_LENGTH;
  const remainingChars = MIN_REPORT_DESCRIPTION_LENGTH - reportDescription.length;
  const descriptionHelperText =
    !isDescriptionValid && reportDescription.length > 0
      ? `${remainingChars} more character${remainingChars === 1 ? "" : "s"} required`
      : undefined;

  const shiftId = shift?.shiftId;
  const workerId = shift?.workerId ?? workerIdProperty;
  const workerName = shift?.workerName ?? workerNameProperty;
  const facilityId = facilityIdProperty ?? profile?.userId;
  const qualificationLabel = shift?.workerQualification ?? workerQualificationProperty;
  const workerFirstName = workerName?.split(" ")[0];

  const logModalEvent = (action: string, extra?: Record<string, unknown>) => {
    logEvent(APP_V2_USER_EVENTS.WORKER_CANCEL_REPORT_MODAL_ACTIONED, {
      action,
      workplaceId: facilityId,
      workplaceUserId: user?._id,
      workerId,
      shiftId,
      ...extra,
    });
  };

  const handleClose = () => {
    logModalEvent("modal_dismissed");
    modalState.closeModal();
  };

  const handleConfirm = async () => {
    if (!isDefined(workerId)) {
      return;
    }

    try {
      await sendWorkerCancelAlert({
        shiftId,
        workerId,
        facilityId,
        shouldReplaceWorker,
        reportDescription,
      });
      logModalEvent("report_submitted", { shouldReplaceWorker, reportDescription });
      const message = shouldReplaceWorker
        ? "Your report has been submitted. We'll review it and find a replacement worker shortly."
        : "Your report has been submitted. Our team will review it and get back to you shortly.";
      showInfoToast(message);
      modalState.closeModal();
    } catch {
      showErrorToast("Unable to send notification. Please contact support.");
    }
  };

  return (
    <ResponsiveDialog
      modalState={modalState}
      title="Report professional asking you to cancel"
      width={480}
      footerContent={
        <DialogFooter
          secondaryAction={{ label: "Nevermind", onClick: handleClose, disabled: isLoading }}
          primaryAction={{
            label: "Submit report",
            isLoading,
            disabled: isLoading || !isDescriptionValid || !reportAcknowledged,
            onClick: () => {
              void handleConfirm();
            },
          }}
        />
      }
      onClose={handleClose}
    >
      <Stack spacing={4}>
        {/* Worker / shift card */}
        <Box
          sx={(theme) => ({
            border: `1px solid ${theme.palette.border?.base}`,
            borderRadius: theme.borderRadius?.xLarge,
            padding: 4,
          })}
        >
          <Stack direction="row" alignItems="center" spacing={3}>
            <Avatar alt={workerName ?? ""} size="medium" src={workerAvatarUrl} />
            <Box>
              {workerName && (
                <Text semibold variant="body2">
                  {workerName}
                </Text>
              )}
              {qualificationLabel && (
                <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                  {qualificationLabel}
                </Text>
              )}
            </Box>
          </Stack>
          {isDefined(shift) && (
            <>
              <Divider sx={{ marginY: 4, marginX: -4, width: "auto" }} />
              <Stack direction="row" alignItems="center" spacing={3}>
                {shift.timeSlot && <TimeSlotAvatar timeSlot={shift.timeSlot} />}
                <Box>
                  <Text semibold variant="body2">
                    {`${formatTime(parseISO(shift.startTime), { timeZone: shift.timezone })} - ${formatTime(parseISO(shift.endTime), { timeZone: shift.timezone })}`}
                  </Text>
                  <Text variant="caption" sx={(theme) => ({ color: theme.palette.text.disabled })}>
                    {formatLongDate(parseISO(shift.startTime), { timeZone: shift.timezone })}
                  </Text>
                </Box>
              </Stack>
            </>
          )}
        </Box>

        {/* Info text */}
        <Text semibold variant="body1" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          Use this form to report a professional who asked you to cancel their shift. This may
          indicate a policy violation.
        </Text>

        {/* Radio group */}
        <RadioGroup
          value={String(shouldReplaceWorker)}
          onChange={(event) => {
            const needsReplacement = event.target.value === "true";
            setShouldReplaceWorker(needsReplacement);
            logModalEvent(
              needsReplacement ? "replacement_option_selected" : "no_replacement_option_selected"
            );
          }}
        >
          <FormControlLabel
            value="true"
            control={<Radio />}
            label="I need a replacement for this shift"
          />
          <FormControlLabel
            value="false"
            control={<Radio />}
            label="I no longer need this shift."
          />
        </RadioGroup>

        {/* Description field */}
        <TextField
          multiline
          fullWidth
          label="Please describe what happened"
          placeholder="Provide details about how the worker requested cancellation (e.g., via phone call, in person, etc.)"
          minRows={3}
          maxRows={6}
          value={reportDescription}
          error={reportDescription.length > 0 && !isDescriptionValid}
          helperText={descriptionHelperText}
          FormHelperTextProps={{
            sx: { color: "error.main" },
          }}
          onChange={(event) => setReportDescription(event.target.value)}
        />

        <ReportAcknowledgmentSection
          workerFirstName={workerFirstName}
          acknowledged={reportAcknowledged}
          onAcknowledgedChange={setReportAcknowledged}
        />

        <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
          The Clipboard Support Team will review the reported issue within 10 minutes.
        </Text>
      </Stack>
    </ResponsiveDialog>
  );
}
